import { AlertTriangle, CheckCircle2, Eye, Pencil, ReceiptText, RotateCcw, Trash2, UploadCloud, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppShell from '../app/AppShell';
import { getPortalRole } from '../app/businessContext';
import {
  CustomDatePicker,
  CustomSearchInput,
  CustomSelect,
  FeedbackMessage,
  ModalBackdrop,
  PaginationFooter,
  SaveConfirmationModal,
} from '../components/common';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import {
  createStaffCashAdvance,
  createStaffExpense,
  deleteStaffCashAdvance,
  deleteStaffExpense,
  fetchExpensesDashboard,
  reviewStaffExpense,
  updateStaffCashAdvance,
  updateStaffExpense,
} from '../services/expenseService';
import { subscribeToExpenseChanges } from '../services/expenseRealtimeService';
import { convertImageFileToWebpDataUrl } from '../utils/imageConverter';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const TABLE_PAGE_SIZE = 10;

function createInitialFilters() {
  return { search: '', staffId: '', status: 'all', date: '', year: String(getLocalCurrentYear()) };
}

function getLocalTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getLocalCurrentYear() {
  return new Date().getFullYear();
}

function createInitialCashAdvanceForm() {
  return {
    staffName: '',
    amount: '',
    dateIssued: getLocalTodayIsoDate(),
    purpose: '',
    notes: '',
    proofImageData: '',
  };
}

function createInitialExpenseForm() {
  return {
    staffCashAdvanceId: '',
    expenseDate: getLocalTodayIsoDate(),
    category: '',
    amount: '',
    description: '',
    proofImageData: '',
  };
}

function createCashAdvanceEditForm(record = {}) {
  return {
    amount: record.amount ? formatAmountInput(record.amount) : '',
    dateIssued: record.dateIssued || getLocalTodayIsoDate(),
    purpose: record.purpose || '',
    notes: record.notes || '',
    proofImageData: '',
  };
}

function createExpenseEditForm(record = {}) {
  return {
    staffCashAdvanceId: record.staffCashAdvanceId ? String(record.staffCashAdvanceId) : '',
    expenseDate: record.expenseDate || getLocalTodayIsoDate(),
    category: record.category || '',
    amount: record.amount ? formatAmountInput(record.amount) : '',
    description: record.description || '',
    proofImageData: '',
  };
}

function ExpensesPage({ navigationItems, activePath, onNavigate }) {
  const saveConfirmation = useSaveConfirmation();
  const portalRole = getPortalRole();
  const isStaffView = portalRole === 'staff' || activePath?.startsWith('/staff');
  const [filters, setFilters] = useState(createInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(createInitialFilters);
  const [dashboard, setDashboard] = useState({
    cashAdvances: [],
    expenses: [],
    staffOptions: [],
    categories: [],
    summary: {},
  });
  const [cashAdvanceForm, setCashAdvanceForm] = useState(createInitialCashAdvanceForm);
  const [cashAdvanceErrors, setCashAdvanceErrors] = useState({});
  const [expenseForm, setExpenseForm] = useState(createInitialExpenseForm);
  const [expenseErrors, setExpenseErrors] = useState({});
  const [activeExpenseTab, setActiveExpenseTab] = useState('cash-advances');
  const [editingCashAdvance, setEditingCashAdvance] = useState(null);
  const [cashAdvanceEditForm, setCashAdvanceEditForm] = useState(createCashAdvanceEditForm);
  const [cashAdvanceEditErrors, setCashAdvanceEditErrors] = useState({});
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseEditForm, setExpenseEditForm] = useState(createExpenseEditForm);
  const [expenseEditErrors, setExpenseEditErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [warningModal, setWarningModal] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const proofUploadScrollTopRef = useRef(0);

  const loadDashboard = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
      setMessage(null);
    }

    try {
      const nextDashboard = await fetchExpensesDashboard(appliedFilters);
      setDashboard(nextDashboard);
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    return subscribeToExpenseChanges(() => {
      loadDashboard(false);
    });
  }, [loadDashboard]);

  const uniqueStaffOptions = useMemo(
    () => dedupeStaffOptions(dashboard.staffOptions),
    [dashboard.staffOptions]
  );
  const staffOptions = useMemo(
    () =>
      uniqueStaffOptions.map((staff) => ({
        value: staff.id,
        label: staff.name,
      })),
    [uniqueStaffOptions]
  );
  const staffSuggestions = useMemo(
    () =>
      uniqueStaffOptions.map((staff) => ({
        value: staff.name,
        label: staff.name,
        searchValue: `${staff.name} ${staff.id}`,
        staffId: staff.id,
      })),
    [uniqueStaffOptions]
  );
  const categoryOptions = useMemo(
    () => dashboard.categories.map((category) => ({ value: category, label: category })),
    [dashboard.categories]
  );
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const openCashAdvanceOptions = useMemo(
    () =>
      dashboard.cashAdvances
        .filter((cashAdvance) => cashAdvance.status !== 'Cancelled')
        .filter((cashAdvance) => Number(cashAdvance.projectedRemainingAmount ?? cashAdvance.remainingAmount ?? 0) > 0)
        .map((cashAdvance) => ({
          value: cashAdvance.id,
          label: `${cashAdvance.staffName} - ${formatCurrency(cashAdvance.projectedRemainingAmount)} available`,
          description: `${cashAdvance.dateIssued} / ${cashAdvance.purpose || 'Cash advance'}`,
        })),
    [dashboard.cashAdvances]
  );
  const searchSuggestions = useMemo(
    () => buildSearchSuggestions(dashboard.cashAdvances, dashboard.expenses),
    [dashboard.cashAdvances, dashboard.expenses]
  );

  function handleFilterChange(event) {
    setFilters((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }

  function applyFilters(event) {
    event?.preventDefault();
    setAppliedFilters(filters);
  }

  function resetFilters() {
    const nextFilters = createInitialFilters();

    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
  }

  function handleCashAdvanceChange(event) {
    const { name } = event.target;
    const value = name === 'amount' ? formatAmountInput(event.target.value) : event.target.value;
    const suggestion = event.suggestion;
    const matchedStaffId = findStaffIdByName(uniqueStaffOptions, value);

    setCashAdvanceErrors((current) => removeErrorKeys(current, name, 'form'));
    setCashAdvanceForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'staffName'
        ? { staffId: suggestion?.staffId || matchedStaffId || '' }
        : {}),
    }));
  }

  function handleExpenseChange(event) {
    const { name } = event.target;
    const value = name === 'amount' ? formatAmountInput(event.target.value) : event.target.value;

    setExpenseErrors((current) => removeErrorKeys(current, name, 'form'));
    setExpenseForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function resetCashAdvanceForm() {
    setCashAdvanceForm(createInitialCashAdvanceForm());
    setCashAdvanceErrors({});
    setMessage(null);
  }

  function resetExpenseForm() {
    setExpenseForm(createInitialExpenseForm());
    setExpenseErrors({});
    setMessage(null);
  }

  function openCashAdvanceEdit(record) {
    setEditingCashAdvance(record);
    setCashAdvanceEditForm(createCashAdvanceEditForm(record));
    setCashAdvanceEditErrors({});
  }

  function openExpenseForCashAdvance(record) {
    setExpenseForm((current) => ({
      ...current,
      staffCashAdvanceId: String(record.id || ''),
    }));
    setExpenseErrors((current) => removeErrorKeys(current, 'staffCashAdvanceId', 'amount', 'form'));
    setActiveExpenseTab('expense-reports');
  }

  function closeCashAdvanceEdit() {
    if (isSaving) {
      return;
    }

    setEditingCashAdvance(null);
    setCashAdvanceEditForm(createCashAdvanceEditForm());
    setCashAdvanceEditErrors({});
  }

  function openExpenseEdit(record) {
    setEditingExpense(record);
    setExpenseEditForm(createExpenseEditForm(record));
    setExpenseEditErrors({});
  }

  function closeExpenseEdit() {
    if (isSaving) {
      return;
    }

    setEditingExpense(null);
    setExpenseEditForm(createExpenseEditForm());
    setExpenseEditErrors({});
  }

  function handleCashAdvanceEditChange(event) {
    const { name } = event.target;
    const value = name === 'amount' ? formatAmountInput(event.target.value) : event.target.value;

    setCashAdvanceEditErrors((current) => removeErrorKeys(current, name, 'form'));
    setCashAdvanceEditForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleExpenseEditChange(event) {
    const { name } = event.target;
    const value = name === 'amount' ? formatAmountInput(event.target.value) : event.target.value;

    setExpenseEditErrors((current) => removeErrorKeys(current, name, 'form'));
    setExpenseEditForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function rememberProofUploadScroll(event) {
    const scrollContainer =
      event.currentTarget?.closest?.('[data-app-scroll-container]') ||
      document.querySelector('[data-app-scroll-container]');

    proofUploadScrollTopRef.current = scrollContainer?.scrollTop || 0;
  }

  function restoreProofUploadScroll() {
    const applyScrollPosition = () => {
      const scrollContainer = document.querySelector('[data-app-scroll-container]');

      if (scrollContainer) {
        scrollContainer.scrollTop = proofUploadScrollTopRef.current;
      }

      window.scrollTo(0, 0);
    };

    applyScrollPosition();
    window.requestAnimationFrame(applyScrollPosition);
    window.setTimeout(applyScrollPosition, 0);
    window.setTimeout(applyScrollPosition, 80);
  }

  async function handleProofChange(event, setter) {
    const file = event.target.files?.[0];

    if (!file) {
      restoreProofUploadScroll();
      return;
    }

    try {
      const converted = await convertImageFileToWebpDataUrl(file, { quality: 0.85 });
      setter((current) => ({ ...current, proofImageData: converted.dataUrl }));
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      event.target.value = '';
      restoreProofUploadScroll();
    }
  }

  async function submitCashAdvance(event) {
    event.preventDefault();
    const nextErrors = validateCashAdvanceForm(cashAdvanceForm);

    if (Object.keys(nextErrors).length) {
      setCashAdvanceErrors(nextErrors);
      setMessage(null);
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setCashAdvanceErrors({});

    try {
      await createStaffCashAdvance({
        ...cashAdvanceForm,
        amount: parseAmountValue(cashAdvanceForm.amount),
        staffId: cashAdvanceForm.staffId || cashAdvanceForm.staffName,
      });
      setCashAdvanceForm(createInitialCashAdvanceForm());
      setCashAdvanceErrors({});
      setMessage({ tone: 'success', text: 'Staff cash advance released.' });
      await loadDashboard();
    } catch (error) {
      setCashAdvanceErrors(mapCashAdvanceServerError(error.message));
    } finally {
      setIsSaving(false);
    }
  }

  async function submitExpense(event) {
    event.preventDefault();
    const nextErrors = validateExpenseForm(
      expenseForm,
      findCashAdvanceById(dashboard.cashAdvances, expenseForm.staffCashAdvanceId)
    );

    if (Object.keys(nextErrors).length) {
      setExpenseErrors(nextErrors);
      setMessage(null);
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setExpenseErrors({});

    try {
      await createStaffExpense({
        ...expenseForm,
        amount: parseAmountValue(expenseForm.amount),
      });
      setExpenseForm(createInitialExpenseForm());
      setExpenseErrors({});
      setMessage({ tone: 'success', text: 'Expense submitted for review.' });
      await loadDashboard();
    } catch (error) {
      setExpenseErrors(mapExpenseServerError(error.message));
    } finally {
      setIsSaving(false);
    }
  }

  async function submitCashAdvanceEdit(event) {
    event.preventDefault();
    const nextErrors = validateCashAdvanceEditForm(cashAdvanceEditForm, editingCashAdvance);

    if (Object.keys(nextErrors).length) {
      setCashAdvanceEditErrors(nextErrors);
      setMessage(null);
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setCashAdvanceEditErrors({});

    try {
      await updateStaffCashAdvance(editingCashAdvance.id, {
        ...cashAdvanceEditForm,
        amount: parseAmountValue(cashAdvanceEditForm.amount),
      });
      setEditingCashAdvance(null);
      setCashAdvanceEditForm(createCashAdvanceEditForm());
      setMessage({ tone: 'success', text: 'Staff cash advance updated.' });
      await loadDashboard();
    } catch (error) {
      setCashAdvanceEditErrors(mapCashAdvanceEditServerError(error.message));
    } finally {
      setIsSaving(false);
    }
  }

  async function submitExpenseEdit(event) {
    event.preventDefault();
    const selectedCashAdvance = findCashAdvanceById(
      dashboard.cashAdvances,
      expenseEditForm.staffCashAdvanceId
    );
    const nextErrors = validateExpenseForm(expenseEditForm, selectedCashAdvance, {
      currentExpense: editingExpense,
      requireProof: false,
    });

    if (Object.keys(nextErrors).length) {
      setExpenseEditErrors(nextErrors);
      setMessage(null);
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setExpenseEditErrors({});

    try {
      await updateStaffExpense(editingExpense.id, {
        ...expenseEditForm,
        amount: parseAmountValue(expenseEditForm.amount),
      });
      setEditingExpense(null);
      setExpenseEditForm(createExpenseEditForm());
      setMessage({ tone: 'success', text: 'Expense report updated.' });
      await loadDashboard();
    } catch (error) {
      setExpenseEditErrors(mapExpenseServerError(error.message));
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteExpenseReport(expense) {
    setIsSaving(true);
    setMessage(null);

    try {
      await deleteStaffExpense(expense.id);
      setMessage({ tone: 'success', text: 'Expense report deleted.' });
      await loadDashboard();
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  function requestCashAdvanceConfirmation(event) {
    event.preventDefault();
    const nextErrors = validateCashAdvanceForm(cashAdvanceForm);

    if (Object.keys(nextErrors).length) {
      setCashAdvanceErrors(nextErrors);
      setMessage(null);
      return;
    }

    saveConfirmation.requestConfirmation(event, submitCashAdvance, {
      title: 'Release staff C.A.?',
      message: 'Please confirm the staff, amount, date, and transfer proof before saving this cash advance.',
      confirmLabel: 'Release C.A.',
      confirmingLabel: 'Releasing...',
    });
  }

  function requestExpenseConfirmation(event) {
    event.preventDefault();
    const nextErrors = validateExpenseForm(
      expenseForm,
      findCashAdvanceById(dashboard.cashAdvances, expenseForm.staffCashAdvanceId)
    );

    if (Object.keys(nextErrors).length) {
      setExpenseErrors(nextErrors);
      setMessage(null);
      return;
    }

    saveConfirmation.requestConfirmation(event, submitExpense, {
      title: 'Submit expense?',
      message: 'Please confirm the cash advance, category, amount, date, and receipt before submitting this expense.',
      confirmLabel: 'Submit Expense',
      confirmingLabel: 'Submitting...',
    });
  }

  function requestCashAdvanceEditConfirmation(event) {
    event.preventDefault();
    const nextErrors = validateCashAdvanceEditForm(cashAdvanceEditForm, editingCashAdvance);

    if (Object.keys(nextErrors).length) {
      setCashAdvanceEditErrors(nextErrors);
      setMessage(null);
      return;
    }

    saveConfirmation.requestConfirmation(event, submitCashAdvanceEdit, {
      title: 'Update staff C.A.?',
      message:
        'Please confirm the updated C.A. details. The amount cannot be lower than already approved expenses.',
      confirmLabel: 'Update C.A.',
      confirmingLabel: 'Updating...',
    });
  }

  function requestExpenseEditConfirmation(event) {
    event.preventDefault();
    const nextErrors = validateExpenseForm(
      expenseEditForm,
      findCashAdvanceById(dashboard.cashAdvances, expenseEditForm.staffCashAdvanceId),
      {
        currentExpense: editingExpense,
        requireProof: false,
      }
    );

    if (Object.keys(nextErrors).length) {
      setExpenseEditErrors(nextErrors);
      setMessage(null);
      return;
    }

    saveConfirmation.requestConfirmation(event, submitExpenseEdit, {
      title: 'Update expense report?',
      message: 'Please confirm the corrected expense details before updating this report.',
      confirmLabel: 'Update Expense',
      confirmingLabel: 'Updating...',
    });
  }

  function requestExpenseDeleteConfirmation(expense) {
    saveConfirmation.requestConfirmation(null, () => deleteExpenseReport(expense), {
      title: 'Delete expense report?',
      message: `This will remove the ${formatCurrency(expense.amount)} ${expense.category || 'expense'} report from ${expense.staffName || 'staff'}.`,
      confirmLabel: 'Delete',
      confirmingLabel: 'Deleting...',
      cancelLabel: 'Keep',
    });
  }

  function requestCashAdvanceDeleteConfirmation(cashAdvance) {
    saveConfirmation.requestConfirmation(null, () => deleteCashAdvanceRecord(cashAdvance), {
      title: 'Delete cash advance?',
      message: `This will remove the ${formatCurrency(cashAdvance.amount)} cash advance for ${cashAdvance.staffName || 'staff'}. It cannot be deleted if expense reports were already submitted for it.`,
      confirmLabel: 'Delete',
      confirmingLabel: 'Deleting...',
      cancelLabel: 'Keep',
    });
  }

  function requestExpenseReviewConfirmation(expense, status) {
    const isApproval = status === 'Approved';
    const isReopen = status === 'Pending';

    saveConfirmation.requestConfirmation(null, () => reviewExpense(expense, status), {
      title: isReopen ? 'Reopen expense?' : isApproval ? 'Approve expense?' : 'Reject expense?',
      message: isReopen
        ? `This will move the approved ${formatCurrency(expense.amount)} ${expense.category || 'expense'} report from ${expense.staffName || 'staff'} back to Pending so it can be edited or deleted.`
        : `Please confirm you want to ${status.toLowerCase()} this ${formatCurrency(expense.amount)} ${expense.category || 'expense'} submitted by ${expense.staffName || 'staff'}.`,
      confirmLabel: isReopen ? 'Reopen' : isApproval ? 'Approve' : 'Reject',
      confirmingLabel: isReopen ? 'Reopening...' : isApproval ? 'Approving...' : 'Rejecting...',
      cancelLabel: 'Review',
    });
  }

  async function reviewExpense(expense, status) {
    setIsSaving(true);
    setMessage(null);

    try {
      await reviewStaffExpense(expense.id, { status });
      setMessage({ tone: 'success', text: status === 'Pending' ? 'Expense reopened.' : `Expense ${status.toLowerCase()}.` });
      await loadDashboard();
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteCashAdvanceRecord(cashAdvance) {
    setIsSaving(true);
    setMessage(null);

    try {
      await deleteStaffCashAdvance(cashAdvance.id);
      setMessage({ tone: 'success', text: 'Cash advance deleted.' });
      await loadDashboard();
    } catch (error) {
      if (isCashAdvanceDeleteBlockedMessage(error.message)) {
        setWarningModal({
          title: 'Delete blocked',
          message: error.message,
          detail: 'A cash advance with submitted expense reports must stay in the records for audit and balance tracking.',
        });
      } else {
        setMessage({ tone: 'error', text: error.message });
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AppShell navigationItems={navigationItems} activePath={activePath} onNavigate={onNavigate}>
      <section className="mt-5 px-4 text-[var(--theme-text)] sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="m-0 text-[0.75rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
                Operations
              </p>
              <h1 className="m-0 mt-1 text-[1.65rem] font-semibold tracking-[-0.02em] text-[var(--theme-text-strong)]">
                Expenses
              </h1>
            </div>
            <span className="rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]">
              {isStaffView ? 'Staff expense submission' : 'Admin C.A. and review'}
            </span>
          </header>

          {message ? <FeedbackMessage tone={message.tone} message={message.text} /> : null}

          <section className="grid gap-3 md:grid-cols-4">
            <SummaryCard label="C.A. Released" value={formatCurrency(dashboard.summary.releasedTotal)} />
            <SummaryCard label="Approved Expenses" value={formatCurrency(dashboard.summary.approvedTotal)} />
            <SummaryCard label="Pending Review" value={formatCurrency(dashboard.summary.pendingTotal)} />
            <SummaryCard label="Remaining Balance" value={formatCurrency(dashboard.summary.remainingTotal)} />
          </section>

          <form
            className="grid gap-3 rounded-[8px] border border-[var(--theme-border)] bg-white p-3 md:grid-cols-[minmax(0,1fr)_190px_130px_170px_140px_auto]"
            onSubmit={applyFilters}
          >
            <CustomSearchInput
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search staff, category, purpose"
              suggestions={searchSuggestions}
            />
            {!isStaffView ? (
              <CustomSelect
                label="Staff"
                name="staffId"
                value={filters.staffId}
                onChange={handleFilterChange}
                options={staffOptions}
                placeholder="All staff"
                clearOptionLabel="All staff"
              />
            ) : null}
            <CustomDatePicker
              label="Date"
              name="date"
              value={filters.date}
              onChange={handleFilterChange}
              placeholder="Any date"
            />
            <CustomSelect
              label="Year"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              options={yearOptions}
              placeholder="Select year"
            />
            <CustomSelect
              label="Status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              options={[
                { value: 'all', label: 'All' },
                { value: 'Pending', label: 'Pending' },
                { value: 'Approved', label: 'Approved' },
                { value: 'Rejected', label: 'Rejected' },
              ]}
            />
            <div className="flex items-end gap-2">
              <button className="h-10 rounded-full bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white" type="submit">
                Apply
              </button>
              <button className="h-10 rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)]" type="button" onClick={resetFilters}>
                Clear
              </button>
            </div>
          </form>

          <ExpenseTabs activeTab={activeExpenseTab} onChange={setActiveExpenseTab} />

          {activeExpenseTab === 'cash-advances' ? (
            <section className={`grid items-stretch gap-4 ${isStaffView ? '' : 'xl:grid-cols-[420px_minmax(0,1fr)]'}`}>
              {!isStaffView ? (
                <ExpensePanel
                  title="Release Staff C.A."
                  action={
                    <button
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                      type="button"
                      onClick={resetCashAdvanceForm}
                      disabled={isSaving}
                      aria-label="Reset cash advance form"
                      title="Reset form"
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    </button>
                  }
                >
                  <form className="grid gap-2.5" onSubmit={requestCashAdvanceConfirmation}>
                    <CustomSearchInput
                      label="Staff"
                      name="staffName"
                      value={cashAdvanceForm.staffName}
                      onChange={handleCashAdvanceChange}
                      placeholder="Type staff name"
                      suggestions={staffSuggestions}
                      hasError={Boolean(cashAdvanceErrors.staffName)}
                      showSuggestionsOnFocus
                    />
                    <FormGrid>
                      <TextField
                        label="Amount"
                        name="amount"
                        value={cashAdvanceForm.amount}
                        onChange={handleCashAdvanceChange}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        hasError={Boolean(cashAdvanceErrors.amount)}
                      />
                      <CustomDatePicker
                        label="Date"
                        name="dateIssued"
                        value={cashAdvanceForm.dateIssued}
                        onChange={handleCashAdvanceChange}
                        error={cashAdvanceErrors.dateIssued}
                        triggerClassName="!min-h-[36px] !py-1.5"
                      />
                    </FormGrid>
                    <TextField label="Purpose" name="purpose" value={cashAdvanceForm.purpose} onChange={handleCashAdvanceChange} />
                    <ProofPicker
                      label="Transfer Proof"
                      hasProof={Boolean(cashAdvanceForm.proofImageData)}
                      hasError={Boolean(cashAdvanceErrors.proofImageData)}
                      previewImage={cashAdvanceForm.proofImageData}
                      onChange={(event) => {
                        setCashAdvanceErrors((current) => removeErrorKeys(current, 'proofImageData', 'form'));
                        handleProofChange(event, setCashAdvanceForm);
                      }}
                      onBeforeOpen={rememberProofUploadScroll}
                      onPreview={setPreviewImage}
                    />
                    <TextareaField label="Notes" name="notes" value={cashAdvanceForm.notes} onChange={handleCashAdvanceChange} />
                    <FormErrorSummary errors={cashAdvanceErrors} />
                    <button className="min-h-[38px] rounded-full bg-[var(--theme-accent)] px-5 font-semibold text-white disabled:opacity-60" disabled={isSaving} type="submit">
                      Save Cash Advance
                    </button>
                  </form>
                </ExpensePanel>
              ) : null}

              <ExpensePanel title="Cash Advances">
                <CashAdvanceTable
                  records={dashboard.cashAdvances}
                  onEdit={!isStaffView ? openCashAdvanceEdit : null}
                  onDelete={!isStaffView ? requestCashAdvanceDeleteConfirmation : null}
                  onSubmitExpense={openExpenseForCashAdvance}
                  onPreview={setPreviewImage}
                  isLoading={isLoading}
                />
              </ExpensePanel>
            </section>
          ) : (
            <section className="grid items-stretch gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
              <ExpensePanel
                title="Submit Expense"
                action={
                  <button
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                    type="button"
                    onClick={resetExpenseForm}
                    disabled={isSaving}
                    aria-label="Reset expense form"
                    title="Reset form"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  </button>
                }
              >
                <form className="grid gap-2.5" onSubmit={requestExpenseConfirmation}>
                  <CustomSelect
                    label="Cash Advance"
                    name="staffCashAdvanceId"
                    value={expenseForm.staffCashAdvanceId}
                    onChange={handleExpenseChange}
                    options={openCashAdvanceOptions}
                    placeholder="Select C.A."
                    error={expenseErrors.staffCashAdvanceId}
                    errorClassName="hidden"
                    triggerClassName="!min-h-[36px] !py-1.5"
                  />
                  <FormGrid>
                    <CustomSelect
                      label="Category"
                      name="category"
                      value={expenseForm.category}
                      onChange={handleExpenseChange}
                      options={categoryOptions}
                      placeholder="Select category"
                      error={expenseErrors.category}
                      errorClassName="hidden"
                      triggerClassName="!min-h-[36px] !py-1.5"
                    />
                    <TextField
                      label="Amount"
                      name="amount"
                      value={expenseForm.amount}
                      onChange={handleExpenseChange}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      hasError={Boolean(expenseErrors.amount)}
                    />
                  </FormGrid>
                  <FormGrid>
                    <CustomDatePicker
                      label="Date"
                      name="expenseDate"
                      value={expenseForm.expenseDate}
                      onChange={handleExpenseChange}
                      error={expenseErrors.expenseDate}
                      triggerClassName="!min-h-[36px] !py-1.5"
                    />
                  </FormGrid>
                  <ProofPicker
                    label="Receipt / Slip"
                    hasProof={Boolean(expenseForm.proofImageData)}
                    hasError={Boolean(expenseErrors.proofImageData)}
                    previewImage={expenseForm.proofImageData}
                    onChange={(event) => {
                      setExpenseErrors((current) => removeErrorKeys(current, 'proofImageData', 'form'));
                      handleProofChange(event, setExpenseForm);
                    }}
                    onBeforeOpen={rememberProofUploadScroll}
                    onPreview={setPreviewImage}
                  />
                  <TextareaField label="Description" name="description" value={expenseForm.description} onChange={handleExpenseChange} />
                  <FormErrorSummary errors={expenseErrors} />
                  <button className="min-h-[38px] rounded-full bg-[var(--theme-accent)] px-5 font-semibold text-white disabled:opacity-60" disabled={isSaving} type="submit">
                    Submit Expense
                  </button>
                </form>
              </ExpensePanel>

              <ExpensePanel title="Expense Reports">
                <ExpenseTable
                  records={dashboard.expenses}
                  isAdmin={!isStaffView}
                  isSaving={isSaving}
                  onDelete={requestExpenseDeleteConfirmation}
                  onEdit={openExpenseEdit}
                  onPreview={setPreviewImage}
                  onReview={requestExpenseReviewConfirmation}
                  isLoading={isLoading}
                />
              </ExpensePanel>
            </section>
          )}
        </div>
      </section>

      {previewImage ? (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/55 px-4" onClick={() => setPreviewImage(null)}>
          <button
            className="fixed right-5 top-5 z-[81] grid h-11 w-11 place-items-center rounded-full border border-white/35 bg-white text-[1.05rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_14px_30px_rgba(0,0,0,0.22)] hover:bg-[var(--theme-surface-soft)]"
            type="button"
            aria-label="Close image preview"
            onClick={() => setPreviewImage(null)}
          >
            X
          </button>
          <div className="max-h-[88vh] max-w-4xl overflow-auto rounded-[8px] bg-white p-3" onClick={(event) => event.stopPropagation()}>
            <img className="block max-h-[78vh] max-w-full rounded-[6px]" src={previewImage} alt="Expense proof" />
          </div>
        </div>
      ) : null}
      {editingCashAdvance ? (
        <CashAdvanceEditModal
          cashAdvance={editingCashAdvance}
          errors={cashAdvanceEditErrors}
          form={cashAdvanceEditForm}
          isSaving={isSaving}
          onCancel={closeCashAdvanceEdit}
          onChange={handleCashAdvanceEditChange}
          onPreview={setPreviewImage}
          onProofChange={(event) => {
            setCashAdvanceEditErrors((current) => removeErrorKeys(current, 'proofImageData', 'form'));
            handleProofChange(event, setCashAdvanceEditForm);
          }}
          onRememberProofScroll={rememberProofUploadScroll}
          onSubmit={requestCashAdvanceEditConfirmation}
        />
      ) : null}
      {editingExpense ? (
        <ExpenseEditModal
          cashAdvanceOptions={openCashAdvanceOptions}
          categoryOptions={categoryOptions}
          errors={expenseEditErrors}
          expense={editingExpense}
          form={expenseEditForm}
          isSaving={isSaving}
          onCancel={closeExpenseEdit}
          onChange={handleExpenseEditChange}
          onPreview={setPreviewImage}
          onProofChange={(event) => {
            setExpenseEditErrors((current) => removeErrorKeys(current, 'proofImageData', 'form'));
            handleProofChange(event, setExpenseEditForm);
          }}
          onRememberProofScroll={rememberProofUploadScroll}
          onSubmit={requestExpenseEditConfirmation}
        />
      ) : null}
      {warningModal ? (
        <ExpenseWarningModal
          title={warningModal.title}
          message={warningModal.message}
          detail={warningModal.detail}
          onClose={() => setWarningModal(null)}
        />
      ) : null}
      <SaveConfirmationModal {...saveConfirmation.modalProps} />
    </AppShell>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-[8px] border border-[var(--theme-border)] bg-white px-4 py-3">
      <span className="text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">{label}</span>
      <strong className="mt-1 block text-[1.1rem] text-[var(--theme-text-strong)]">{value}</strong>
    </div>
  );
}

function ExpenseTabs({ activeTab, onChange }) {
  const tabs = [
    { value: 'cash-advances', label: 'Cash Advances' },
    { value: 'expense-reports', label: 'Expense Reports' },
  ];

  return (
    <div className="flex flex-wrap gap-2 rounded-[8px] border border-[var(--theme-border)] bg-white p-2" role="tablist" aria-label="Expense sections">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value;

        return (
          <button
            key={tab.value}
            className={`min-h-[38px] rounded-full px-4 text-[0.84rem] font-semibold transition ${
              isActive
                ? 'bg-[var(--theme-accent)] text-white shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.14)]'
                : 'border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:bg-[var(--theme-surface-soft)]'
            }`}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function ExpensePanel({ title, action = null, children }) {
  return (
    <section className="min-w-0 rounded-[8px] border border-[var(--theme-border)] bg-white p-4">
      <div className="mb-2.5 flex min-h-9 items-center justify-between gap-3">
        <h2 className="m-0 text-[1rem] font-semibold text-[var(--theme-text-strong)]">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function ExpenseWarningModal({
  title = 'Action blocked',
  message,
  detail = '',
  onClose,
}) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.22)] backdrop-blur-[3px]"
      contentClassName="items-center"
      zIndexClassName="z-[90]"
    >
      <div className="w-full max-w-md rounded-[22px] border border-[#f1d5a9] bg-white p-6 shadow-[0_24px_60px_rgba(var(--theme-shadow-rgb),0.2)]">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-[#fff3db] text-[#b57424]">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h3 className="m-0 text-[1.12rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
              {title}
            </h3>
            <p className="m-0 mt-2 text-[0.94rem] leading-6 text-[var(--theme-text-muted)]">
              {message}
            </p>
            {detail ? (
              <p className="m-0 mt-2 text-[0.88rem] leading-5 text-[var(--theme-text-muted)]">
                {detail}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            className="inline-flex min-h-[42px] items-center justify-center rounded-[14px] bg-[var(--theme-text-subtle)] px-5 text-[0.92rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2"
            type="button"
            onClick={onClose}
          >
            Got it
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function FormGrid({ children }) {
  return <div className="grid gap-2.5 sm:grid-cols-2">{children}</div>;
}

function TextField({ label, hasError = false, ...props }) {
  return (
    <label className="grid gap-1">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`min-h-[36px] rounded-full border bg-white px-3 text-[0.86rem] outline-none ${
          hasError
            ? 'border-[#df6d5f] focus:border-[#c24738] focus:ring-2 focus:ring-[#f7d5cf]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)]'
        }`}
        aria-invalid={hasError}
        {...props}
      />
    </label>
  );
}

function TextareaField({ label, ...props }) {
  return (
    <label className="grid gap-1">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <textarea className="min-h-[56px] resize-none rounded-[8px] border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.86rem] outline-none focus:border-[var(--theme-accent)]" {...props} />
    </label>
  );
}

function ProofPicker({
  label,
  hasProof,
  hasError = false,
  previewImage = '',
  onBeforeOpen,
  onChange,
  onPreview,
}) {
  const inputRef = useRef(null);

  function openFilePicker(event) {
    onBeforeOpen?.(event);
    inputRef.current?.click();
  }

  return (
    <div>
      <div
        className={`flex min-h-[38px] w-full items-center justify-between gap-3 rounded-[8px] border border-dashed bg-[var(--theme-surface-soft)] px-3 text-left text-[0.84rem] font-semibold text-[var(--theme-text-subtle)] ${
          hasError ? 'border-[#df6d5f] ring-2 ring-[#f7d5cf]' : 'border-[var(--theme-border)]'
        }`}
      >
        <button
          className="inline-flex min-h-[32px] min-w-0 flex-1 items-center gap-2 text-left outline-none"
          type="button"
          onClick={openFilePicker}
        >
          <UploadCloud className="h-4 w-4" aria-hidden="true" />
          {hasProof ? `${label} ready` : label}
        </button>
        {previewImage ? (
          <button
            className="grid h-8 w-11 shrink-0 place-items-center overflow-hidden rounded-[6px] border border-[var(--theme-border)] bg-white shadow-[0_4px_12px_rgba(var(--theme-shadow-rgb),0.08)]"
            type="button"
            title="View proof image"
            onClick={() => onPreview?.(previewImage)}
          >
            <img className="h-full w-full object-cover" src={previewImage} alt="Proof preview" />
          </button>
        ) : (
          <button
            className="min-h-[32px] shrink-0 px-1 text-[0.84rem] font-semibold text-[var(--theme-text-subtle)] outline-none"
            type="button"
            onClick={openFilePicker}
          >
            Upload
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        className="hidden"
        type="file"
        accept="image/*"
        tabIndex={-1}
        onChange={onChange}
      />
    </div>
  );
}

function FormErrorSummary({ errors }) {
  const messages = Object.values(errors || {}).filter(Boolean);

  if (!messages.length) {
    return null;
  }

  return (
    <div className="rounded-[8px] border border-[#efc3b8] bg-[#fff6f3] px-3 py-2 text-[0.84rem] font-medium leading-5 text-[#a34334]">
      {messages[0]}
    </div>
  );
}

function CashAdvanceTable({ records, onDelete, onEdit, onSubmitExpense, onPreview, isLoading }) {
  const [currentPage, setCurrentPage] = useState(1);
  const pagination = getTablePagination(records.length, currentPage, setCurrentPage);
  const visibleRecords = records.slice(pagination.startIndex, pagination.endIndex);
  const hasActions = Boolean(onDelete || onEdit || onSubmitExpense);

  useEffect(() => {
    setCurrentPage(1);
  }, [records]);

  if (isLoading) {
    return <FeedbackMessage message="Loading cash advances." />;
  }

  if (!records.length) {
    return <EmptyState text="No staff cash advances found." />;
  }

  return (
    <div className="overflow-hidden rounded-[8px] border border-[var(--theme-border-soft)]">
      <div className="max-w-full overflow-x-auto">
        <table className="w-full min-w-[1240px] table-fixed border-collapse text-[0.84rem]">
          <colgroup>
            <col className="w-[105px]" />
            <col className="w-[90px]" />
            <col className="w-[220px]" />
            <col className="w-[135px]" />
            <col className="w-[135px]" />
            <col className="w-[230px]" />
            {hasActions ? <col className="w-[325px]" /> : null}
          </colgroup>
          <thead>
            <tr className="border-b border-[var(--theme-border)] text-left text-[0.72rem] uppercase tracking-[0.06em] text-[var(--theme-text-muted)]">
              <th className="py-2 pl-4 pr-4">Date</th>
              <th className="px-3">Staff</th>
              <th className="px-3">Purpose</th>
              <th className="px-3 text-right">Amount</th>
              <th className="px-3 text-right">Remaining</th>
              <th className="px-3">Proof</th>
              {hasActions ? <th className="pl-3 pr-4 text-right">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {visibleRecords.map((record) => (
              <tr key={record.id} className="border-b border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-accent-soft)]/45 last:border-b-0">
                <td className="py-2 pl-4 pr-4">{formatDate(record.dateIssued)}</td>
                <td className="truncate px-3">{record.staffName}</td>
                <td className="truncate px-3">{record.purpose || '-'}</td>
                <td className="px-3 text-right">{formatCurrency(record.amount)}</td>
                <td className="px-3 text-right">{formatCurrency(record.remainingAmount)}</td>
                <td className="px-3"><ProofButton record={record} preview={record.proofPreview} onPreview={onPreview} /></td>
                {hasActions ? (
                  <td className="pl-3 pr-4 text-right">
                    <div className="flex flex-nowrap justify-end gap-1.5 whitespace-nowrap">
                      {onSubmitExpense ? (
                        <button
                          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--theme-accent)] px-3 py-1.5 text-[0.78rem] font-semibold text-white shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.14)] transition hover:bg-[var(--theme-accent-strong)]"
                          type="button"
                          onClick={() => onSubmitExpense(record)}
                        >
                          Submit Expense
                        </button>
                      ) : null}
                      {onEdit ? (
                        <button
                          className="inline-flex items-center gap-1.5 rounded-full border border-[var(--theme-border)] px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]"
                          type="button"
                          onClick={() => onEdit(record)}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                          Edit
                        </button>
                      ) : null}
                      {onDelete ? (
                        <button
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#f0b9ac] bg-[#fff3ef] px-3 py-1.5 text-[0.78rem] font-semibold text-[#a34334] transition hover:border-[#e59d8d] hover:bg-[#ffe9e2]"
                          type="button"
                          onClick={() => onDelete(record)}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationFooter {...pagination.footerProps} />
    </div>
  );
}

function CashAdvanceEditModal({
  cashAdvance,
  errors,
  form,
  isSaving,
  onCancel,
  onChange,
  onPreview,
  onProofChange,
  onRememberProofScroll,
  onSubmit,
}) {
  const previewImage = form.proofImageData || cashAdvance.proofPreview;

  return (
    <ModalBackdrop
      backdropClassName="bg-black/35"
      contentClassName="items-center"
      zIndexClassName="z-[70]"
    >
      <form
        className="grid max-h-[88vh] w-full max-w-xl gap-4 overflow-y-auto rounded-[8px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.18)]"
        onSubmit={onSubmit}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-[1.08rem] font-semibold text-[var(--theme-text-strong)]">
              Edit Staff C.A.
            </h3>
            <p className="m-0 mt-1 text-[0.84rem] text-[var(--theme-text-muted)]">
              {cashAdvance.staffName} / approved expenses {formatCurrency(cashAdvance.approvedExpenseAmount)}
            </p>
          </div>
          <button
            className="rounded-full border border-[var(--theme-border)] px-3 py-1.5 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]"
            type="button"
            onClick={onCancel}
            disabled={isSaving}
          >
            Close
          </button>
        </div>

        <FormGrid>
          <TextField
            label="Amount"
            name="amount"
            value={form.amount}
            onChange={onChange}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            hasError={Boolean(errors.amount)}
          />
          <CustomDatePicker
            label="Date"
            name="dateIssued"
            value={form.dateIssued}
            onChange={onChange}
            error={errors.dateIssued}
          />
        </FormGrid>
        <TextField
          label="Purpose"
          name="purpose"
          value={form.purpose}
          onChange={onChange}
        />
        <ProofPicker
          label="Transfer Proof"
          hasProof={Boolean(previewImage)}
          hasError={Boolean(errors.proofImageData)}
          previewImage={previewImage}
          onBeforeOpen={onRememberProofScroll}
          onChange={onProofChange}
          onPreview={onPreview}
        />
        <TextareaField
          label="Notes"
          name="notes"
          value={form.notes}
          onChange={onChange}
        />
        <FormErrorSummary errors={errors} />
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            className="min-h-[42px] rounded-full border border-[var(--theme-border)] bg-white px-5 font-semibold text-[var(--theme-text-subtle)] disabled:opacity-60"
            disabled={isSaving}
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="min-h-[42px] rounded-full bg-[var(--theme-accent)] px-5 font-semibold text-white disabled:opacity-60"
            disabled={isSaving}
            type="submit"
          >
            Update C.A.
          </button>
        </div>
      </form>
    </ModalBackdrop>
  );
}

function ExpenseEditModal({
  cashAdvanceOptions,
  categoryOptions,
  errors,
  expense,
  form,
  isSaving,
  onCancel,
  onChange,
  onPreview,
  onProofChange,
  onRememberProofScroll,
  onSubmit,
}) {
  const previewImage = form.proofImageData || expense.proofPreview;

  return (
    <ModalBackdrop
      backdropClassName="bg-black/35"
      contentClassName="items-center"
      zIndexClassName="z-[70]"
    >
      <form
        className="grid max-h-[88vh] w-full max-w-xl gap-4 overflow-y-auto rounded-[8px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.18)]"
        onSubmit={onSubmit}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-[1.08rem] font-semibold text-[var(--theme-text-strong)]">
              Edit Expense Report
            </h3>
            <p className="m-0 mt-1 text-[0.84rem] text-[var(--theme-text-muted)]">
              {expense.staffName} / {expense.status}
            </p>
          </div>
          <button
            className="rounded-full border border-[var(--theme-border)] px-3 py-1.5 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]"
            type="button"
            onClick={onCancel}
            disabled={isSaving}
          >
            Close
          </button>
        </div>

        <CustomSelect
          label="Cash Advance"
          name="staffCashAdvanceId"
          value={form.staffCashAdvanceId}
          onChange={onChange}
          options={cashAdvanceOptions}
          placeholder="Select C.A."
          error={errors.staffCashAdvanceId}
          errorClassName="hidden"
        />
        <FormGrid>
          <CustomSelect
            label="Category"
            name="category"
            value={form.category}
            onChange={onChange}
            options={categoryOptions}
            placeholder="Select category"
            error={errors.category}
            errorClassName="hidden"
          />
          <TextField
            label="Amount"
            name="amount"
            value={form.amount}
            onChange={onChange}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            hasError={Boolean(errors.amount)}
          />
        </FormGrid>
        <FormGrid>
          <CustomDatePicker
            label="Date"
            name="expenseDate"
            value={form.expenseDate}
            onChange={onChange}
            error={errors.expenseDate}
          />
        </FormGrid>
        <ProofPicker
          label="Receipt / Slip"
          hasProof={Boolean(previewImage)}
          hasError={Boolean(errors.proofImageData)}
          previewImage={previewImage}
          onBeforeOpen={onRememberProofScroll}
          onChange={onProofChange}
          onPreview={onPreview}
        />
        <TextareaField
          label="Description"
          name="description"
          value={form.description}
          onChange={onChange}
        />
        <FormErrorSummary errors={errors} />
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            className="min-h-[42px] rounded-full border border-[var(--theme-border)] bg-white px-5 font-semibold text-[var(--theme-text-subtle)] disabled:opacity-60"
            disabled={isSaving}
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="min-h-[42px] rounded-full bg-[var(--theme-accent)] px-5 font-semibold text-white disabled:opacity-60"
            disabled={isSaving}
            type="submit"
          >
            Update Expense
          </button>
        </div>
      </form>
    </ModalBackdrop>
  );
}

function ExpenseTable({
  records,
  isAdmin,
  isSaving,
  onDelete,
  onEdit,
  onPreview,
  onReview,
  isLoading,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const pagination = getTablePagination(records.length, currentPage, setCurrentPage);
  const visibleRecords = records.slice(pagination.startIndex, pagination.endIndex);
  const baseActionClass =
    'inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[0.78rem] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60';
  const actionClasses = {
    approve:
      `${baseActionClass} border border-[var(--theme-accent)] bg-[var(--theme-accent)] text-white shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.14)] hover:bg-[var(--theme-accent-strong)]`,
    reject:
      `${baseActionClass} border border-[#f0b9ac] bg-[#fff3ef] text-[#a34334] hover:border-[#e59d8d] hover:bg-[#ffe9e2]`,
    edit:
      `${baseActionClass} border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-accent)]`,
    reopen:
      `${baseActionClass} border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-accent)]`,
    delete:
      `${baseActionClass} border border-[#f0b9ac] bg-[#fff3ef] text-[#a34334] hover:border-[#e59d8d] hover:bg-[#ffe9e2]`,
  };
  const tableMinWidthClass = isAdmin ? 'min-w-[1210px]' : 'min-w-[1085px]';
  const proofColumnClass = isAdmin ? 'w-[265px]' : 'w-[235px]';
  const actionsColumnClass = isAdmin ? 'w-[325px]' : 'w-[210px]';

  useEffect(() => {
    setCurrentPage(1);
  }, [records]);

  if (isLoading) {
    return <FeedbackMessage message="Loading expense reports." />;
  }

  if (!records.length) {
    return <EmptyState text="No expense reports found." />;
  }

  return (
    <div className="overflow-hidden rounded-[8px] border border-[var(--theme-border-soft)]">
      <div className="max-w-full overflow-x-auto">
        <table className={`w-full ${tableMinWidthClass} table-fixed border-collapse text-[0.84rem]`}>
          <colgroup>
            <col className="w-[105px]" />
            <col className="w-[100px]" />
            <col className="w-[190px]" />
            <col className="w-[135px]" />
            <col className="w-[110px]" />
            <col className={proofColumnClass} />
            <col className={actionsColumnClass} />
          </colgroup>
          <thead>
            <tr className="border-b border-[var(--theme-border)] text-left text-[0.72rem] uppercase tracking-[0.06em] text-[var(--theme-text-muted)]">
              <th className="py-2 pl-4 pr-4">Date</th>
              <th className="px-3">Staff</th>
              <th className="px-3">Category</th>
              <th className="px-3 text-right">Amount</th>
              <th className="px-3">Status</th>
              <th className="px-3 pr-6">Proof</th>
              <th className="pl-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRecords.map((record) => {
              const isLockedReviewStatus = ['Approved', 'Rejected'].includes(record.status);
              const canEditOrDelete = !isLockedReviewStatus;
              const hasReviewActions = isAdmin && record.status === 'Pending';
              const canReopen = isAdmin && isLockedReviewStatus;

              return (
                <tr key={record.id} className="border-b border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-accent-soft)]/45 last:border-b-0">
                  <td className="py-2 pl-4 pr-4">{formatDate(record.expenseDate)}</td>
                  <td className="truncate px-3">{record.staffName}</td>
                  <td className="truncate px-3">{record.category}</td>
                  <td className="px-3 text-right">{formatCurrency(record.amount)}</td>
                  <td className="px-3"><StatusPill status={record.status} /></td>
                  <td className="px-3 pr-6"><ProofButton record={record} preview={record.proofPreview} onPreview={onPreview} /></td>
                  <td className="pl-3 pr-4 text-right">
                    {canEditOrDelete || canReopen ? (
                      <div className="flex flex-nowrap items-center justify-end gap-2 whitespace-nowrap">
                        {canReopen ? (
                          <button className={actionClasses.reopen} disabled={isSaving} type="button" onClick={() => onReview(record, 'Pending')}>
                            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Reopen
                          </button>
                        ) : null}
                        {hasReviewActions ? (
                          <div className="flex flex-nowrap gap-2">
                            <button className={actionClasses.approve} disabled={isSaving} type="button" onClick={() => onReview(record, 'Approved')}>
                              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Approve
                            </button>
                            <button className={actionClasses.reject} disabled={isSaving} type="button" onClick={() => onReview(record, 'Rejected')}>
                              <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> Reject
                            </button>
                          </div>
                        ) : null}
                        {canEditOrDelete ? (
                          <div className="flex flex-nowrap gap-1.5">
                            <button className={actionClasses.edit} disabled={isSaving} type="button" onClick={() => onEdit(record)}>
                              <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
                            </button>
                            <button className={actionClasses.delete} disabled={isSaving} type="button" onClick={() => onDelete(record)}>
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-[var(--theme-text-muted)]">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PaginationFooter {...pagination.footerProps} />
    </div>
  );
}

function StatusPill({ status }) {
  const tone =
    status === 'Approved'
      ? 'border-[#b9dcc0] bg-[#f2fbf4] text-[#32753e]'
      : status === 'Rejected'
        ? 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]'
        : 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]';

  return <span className={`rounded-full border px-2.5 py-1 text-[0.72rem] font-semibold ${tone}`}>{status}</span>;
}

function ProofButton({ record, preview, onPreview }) {
  const retention = getProofImageRetention(record);

  if (record?.isProofImageExpired) {
    return <span className="text-[0.78rem] font-semibold text-[var(--theme-text-muted)]">Expired</span>;
  }

  if (!preview) {
    return <span className="text-[var(--theme-text-muted)]">-</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button className="inline-flex items-center gap-1.5 rounded-full border border-[var(--theme-border)] px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]" type="button" onClick={() => onPreview(preview)}>
        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
        View
      </button>
      {retention.label ? (
        <span className={`rounded-full border px-2 py-1 text-[0.72rem] font-semibold ${retention.toneClass}`}>
          {retention.label}
        </span>
      ) : null}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="grid place-items-center rounded-[8px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-4 py-10 text-[0.9rem] text-[var(--theme-text-muted)]">
      <ReceiptText className="mb-2 h-6 w-6" aria-hidden="true" />
      {text}
    </div>
  );
}

function validateCashAdvanceForm(values) {
  const errors = {};
  const amount = parseAmountValue(values.amount);

  if (!String(values.staffName || '').trim()) {
    errors.staffName = 'Staff name is required.';
  } else if (!String(values.staffId || '').trim()) {
    errors.staffName = 'Select a staff account from the suggestions.';
  }

  if (!Number.isFinite(amount) || amount <= 0) {
    errors.amount = 'Cash advance amount must be greater than zero.';
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(values.dateIssued || '').trim())) {
    errors.dateIssued = 'Cash advance date is required.';
  }

  if (!values.proofImageData) {
    errors.proofImageData = 'Cash advance transfer proof is required.';
  }

  return errors;
}

function validateCashAdvanceEditForm(values, cashAdvance) {
  const errors = {};
  const amount = parseAmountValue(values.amount);
  const approvedExpenseAmount = Number(cashAdvance?.approvedExpenseAmount || 0);

  if (!Number.isFinite(amount) || amount <= 0) {
    errors.amount = 'Cash advance amount must be greater than zero.';
  } else if (amount < approvedExpenseAmount) {
    errors.amount = `Cash advance amount cannot be lower than approved expenses (${formatCurrency(approvedExpenseAmount)}).`;
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(values.dateIssued || '').trim())) {
    errors.dateIssued = 'Cash advance date is required.';
  }

  return errors;
}

function validateExpenseForm(values, cashAdvance, options = {}) {
  const errors = {};
  const amount = parseAmountValue(values.amount);
  const currentExpense = options.currentExpense || null;
  const currentExpenseUsesCashAdvance =
    currentExpense &&
    String(currentExpense.staffCashAdvanceId) === String(values.staffCashAdvanceId) &&
    ['Approved', 'Pending'].includes(currentExpense.status);
  const availableAmount =
    Number(cashAdvance?.projectedRemainingAmount ?? cashAdvance?.remainingAmount ?? 0) +
    (currentExpenseUsesCashAdvance ? Number(currentExpense.amount || 0) : 0);

  if (!String(values.staffCashAdvanceId || '').trim()) {
    errors.staffCashAdvanceId = 'Cash advance is required.';
  } else if (!cashAdvance) {
    errors.staffCashAdvanceId = 'Selected cash advance was not found.';
  }

  if (!String(values.category || '').trim()) {
    errors.category = 'Expense category is required.';
  }

  if (!Number.isFinite(amount) || amount <= 0) {
    errors.amount = 'Expense amount must be greater than zero.';
  } else if (cashAdvance && amount > availableAmount) {
    errors.amount = `Expense amount cannot exceed available C.A. balance (${formatCurrency(availableAmount)}).`;
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(values.expenseDate || '').trim())) {
    errors.expenseDate = 'Expense date is required.';
  }

  if (options.requireProof !== false && !values.proofImageData) {
    errors.proofImageData = 'Expense proof or receipt is required.';
  }

  return errors;
}

function isCashAdvanceDeleteBlockedMessage(message = '') {
  const normalizedMessage = String(message || '').toLowerCase();

  return (
    normalizedMessage.includes('cash advance') &&
    normalizedMessage.includes('cannot be deleted') &&
    normalizedMessage.includes('expense reports')
  );
}

function findCashAdvanceById(cashAdvances, id) {
  return (cashAdvances || []).find(
    (cashAdvance) => String(cashAdvance.id) === String(id)
  );
}

function buildYearOptions() {
  const currentYear = getLocalCurrentYear();

  return Array.from({ length: 12 }, (_, index) => {
    const year = String(currentYear + 1 - index);

    return { value: year, label: year };
  });
}

function formatAmountInput(value) {
  const text = String(value || '').replace(/,/g, '');
  const hasDecimalPoint = text.includes('.');
  const [rawInteger = '', ...decimalParts] = text.split('.');
  const integerDigits = rawInteger.replace(/\D/g, '');
  const decimalDigits = decimalParts.join('').replace(/\D/g, '').slice(0, 2);
  const normalizedInteger = integerDigits.replace(/^0+(?=\d)/, '');
  const formattedInteger = normalizedInteger
    ? Number(normalizedInteger).toLocaleString('en-US')
    : '';

  if (!formattedInteger && !hasDecimalPoint) {
    return '';
  }

  if (hasDecimalPoint) {
    return `${formattedInteger || '0'}.${decimalDigits}`;
  }

  return formattedInteger;
}

function parseAmountValue(value) {
  const amount = Number(String(value || '').replace(/,/g, ''));

  return Number.isFinite(amount) ? Number(amount.toFixed(2)) : 0;
}

function getTablePagination(totalItems, currentPage, setCurrentPage) {
  const totalPages = Math.max(1, Math.ceil(totalItems / TABLE_PAGE_SIZE));
  const normalizedPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (normalizedPage - 1) * TABLE_PAGE_SIZE;
  const endIndex = Math.min(startIndex + TABLE_PAGE_SIZE, totalItems);

  return {
    startIndex,
    endIndex,
    footerProps: {
      currentPage: normalizedPage,
      endItem: totalItems ? endIndex : 0,
      isNextDisabled: normalizedPage >= totalPages,
      isPreviousDisabled: normalizedPage <= 1,
      onNext: () => setCurrentPage((page) => Math.min(totalPages, page + 1)),
      onPrevious: () => setCurrentPage((page) => Math.max(1, page - 1)),
      startItem: totalItems ? startIndex + 1 : 0,
      totalItems,
      totalPages,
    },
  };
}

function mapCashAdvanceServerError(message) {
  const text = String(message || 'Unable to save cash advance.');
  const normalizedText = text.toLowerCase();

  if (normalizedText.includes('staff name') || normalizedText.includes('staff account')) {
    return { staffName: text };
  }

  if (normalizedText.includes('amount')) {
    return { amount: text };
  }

  if (normalizedText.includes('date')) {
    return { dateIssued: text };
  }

  if (normalizedText.includes('proof')) {
    return { proofImageData: text };
  }

  return { form: text };
}

function mapCashAdvanceEditServerError(message) {
  const text = String(message || 'Unable to update cash advance.');
  const normalizedText = text.toLowerCase();

  if (normalizedText.includes('amount') || normalizedText.includes('approved expenses')) {
    return { amount: text };
  }

  if (normalizedText.includes('date')) {
    return { dateIssued: text };
  }

  if (normalizedText.includes('proof')) {
    return { proofImageData: text };
  }

  return { form: text };
}

function mapExpenseServerError(message) {
  const text = String(message || 'Unable to submit expense.');
  const normalizedText = text.toLowerCase();

  if (normalizedText.includes('cash advance')) {
    return { staffCashAdvanceId: text };
  }

  if (normalizedText.includes('category')) {
    return { category: text };
  }

  if (normalizedText.includes('amount')) {
    return { amount: text };
  }

  if (normalizedText.includes('date')) {
    return { expenseDate: text };
  }

  if (normalizedText.includes('proof') || normalizedText.includes('receipt')) {
    return { proofImageData: text };
  }

  return { form: text };
}

function removeErrorKeys(errors, ...keys) {
  const nextErrors = { ...(errors || {}) };

  keys.forEach((key) => {
    delete nextErrors[key];
  });

  return nextErrors;
}

function buildSearchSuggestions(cashAdvances, expenses) {
  const values = new Set();

  [...cashAdvances, ...expenses].forEach((record) => {
    [record.staffName, record.purpose, record.category, record.description]
      .filter(Boolean)
      .forEach((value) => values.add(String(value)));
  });

  return Array.from(values).map((value) => ({
    value,
    label: value,
    searchValue: value,
  }));
}

function findStaffIdByName(staffOptions, staffName) {
  const match = staffOptions.find(
    (staff) => String(staff.name || '').toLowerCase() === String(staffName || '').toLowerCase()
  );

  return match?.id || '';
}

function dedupeStaffOptions(staffOptions = []) {
  const staffByName = new Map();

  staffOptions.forEach((staff) => {
    const name = String(staff.name || '').trim();
    const key = name.toLowerCase();

    if (!key) {
      return;
    }

    const existingStaff = staffByName.get(key);

    if (!existingStaff || isPreferredStaffOption(staff, existingStaff)) {
      staffByName.set(key, staff);
    }
  });

  return Array.from(staffByName.values());
}

function isPreferredStaffOption(nextStaff, currentStaff) {
  const nextId = String(nextStaff.id || '').trim();
  const currentId = String(currentStaff.id || '').trim();
  const nextHasNumericId = /^\d+$/.test(nextId);
  const currentHasNumericId = /^\d+$/.test(currentId);

  if (nextHasNumericId !== currentHasNumericId) {
    return nextHasNumericId;
  }

  return nextId.length > currentId.length;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getProofImageRetention(record, now = new Date()) {
  const expiresAt = record?.proofImageExpiresAt ? new Date(record.proofImageExpiresAt) : null;
  const remainingMs =
    expiresAt && !Number.isNaN(expiresAt.getTime())
      ? expiresAt.getTime() - now.getTime()
      : null;
  const daysRemaining = Number(record?.proofImageDaysRemaining);

  if (!record?.proofImage || (!Number.isFinite(daysRemaining) && remainingMs === null)) {
    return { label: '', toneClass: '' };
  }

  if (remainingMs !== null && remainingMs > 0 && remainingMs < MS_PER_DAY) {
    return {
      label: `Deletes in ${formatSubDayCountdown(remainingMs)}`,
      toneClass: 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]',
    };
  }

  const normalizedDays =
    remainingMs === null
      ? Math.max(0, daysRemaining)
      : Math.max(0, Math.ceil(remainingMs / MS_PER_DAY));

  return {
    label:
      normalizedDays <= 0
        ? 'Deletes today'
        : `Deletes in ${normalizedDays} day${normalizedDays === 1 ? '' : 's'}`,
    toneClass:
      normalizedDays <= 3
        ? 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]'
        : normalizedDays <= 7
          ? 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]'
          : 'border-[#b9dcc0] bg-[#f2fbf4] text-[#32753e]',
  };
}

function formatSubDayCountdown(remainingMs) {
  const totalMinutes = Math.max(1, Math.ceil(remainingMs / (60 * 1000)));

  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h${minutes ? ` ${minutes}m` : ''} left`;
  }

  return `${totalMinutes}m left`;
}

export default ExpensesPage;
